import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import styles from './NewsUpdatesV2.module.scss';
import { getCompanyLogo, getFaviconUrl, UrlMetadataMap } from '../../../../../../utils/urlMetadata';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox';
import SourceBadge from '../../../../../../common/SourceBadge/SourceBadge';
import { SOURCE_BADGE_TONE } from '../../../../../../common/SourceBadge/constants';
import { OVERVIEW_SOURCE_LABELS } from '../../constants';

interface News {
    imageUrl: string;
    redirectUrl: string;
    publishedDate: string;
    publisherName: string;
    title: string;
    snippet: string;
    newsSentiment: 'Positive' | 'Negative' | 'Neutral';
}

interface NewsReport {
    newsSummary: string;
    news: News[];
}

interface NewsUpdatesV2Props {
    newsReport: NewsReport;
}

// Define a type for tracking image loading status
interface ImageStatus {
    mainImageFailed: boolean;
    faviconFailed: boolean;
}

const NewsUpdatesV2: React.FC<NewsUpdatesV2Props> = ({ newsReport }) => {
    const [websiteMetadata, setWebsiteMetadata] = useState<UrlMetadataMap>({});
    const [imageStatus, setImageStatus] = useState<Record<string, ImageStatus>>({});

    // Return early if newsReport or news array is missing
    if (!newsReport || !newsReport.news || !Array.isArray(newsReport.news) || newsReport.news.length === 0) {
        return null;
    }

    useEffect(() => {
        const metadataMap: UrlMetadataMap = {};

        // Ensure news array exists and is an array before iterating
        if (newsReport.news && Array.isArray(newsReport.news)) {
            newsReport.news.forEach((news) => {
                if (news && news.redirectUrl) {
                    metadataMap[news.redirectUrl] = {
                        image: getCompanyLogo(news.redirectUrl),
                        favicon: getFaviconUrl(news.redirectUrl),
                    };
                }
            });
        }

        setWebsiteMetadata(metadataMap);

        // Reset image status when news report changes
        setImageStatus({});
    }, [newsReport]);

    // Handle main image load error
    const handleMainImageError = (newsIndex: number, redirectUrl: string) => {
        setImageStatus((prevStatus) => ({
            ...prevStatus,
            [newsIndex]: {
                ...prevStatus[newsIndex],
                mainImageFailed: true,
            },
        }));
    };

    // Handle favicon load error
    const handleFaviconError = (newsIndex: number) => {
        setImageStatus((prevStatus) => ({
            ...prevStatus,
            [newsIndex]: {
                ...prevStatus[newsIndex],
                faviconFailed: true,
            },
        }));
    };

    // Function to render the appropriate image or placeholder
    const renderPublisherImage = (newsItem: News, index: number) => {
        const status = imageStatus[index] || { mainImageFailed: false, faviconFailed: false };

        // If no redirectUrl or metadata, show placeholder immediately
        if (!newsItem?.redirectUrl || !websiteMetadata[newsItem.redirectUrl]) {
            return <div className={styles.placeholderImg}>{newsItem?.publisherName?.charAt(0)?.toUpperCase() || 'N'}</div>;
        }

        // If main image failed but we haven't tried favicon yet
        if (status.mainImageFailed && !status.faviconFailed && websiteMetadata[newsItem.redirectUrl]?.favicon) {
            return (
                <img
                    src={websiteMetadata[newsItem.redirectUrl].favicon}
                    alt={newsItem?.publisherName || 'Publisher'}
                    className={styles.publisherImage}
                    onError={() => handleFaviconError(index)}
                />
            );
        }

        // If both main image and favicon failed, show placeholder
        if (status.mainImageFailed && status.faviconFailed) {
            return <div className={styles.placeholderImg}>{newsItem?.publisherName?.charAt(0)?.toUpperCase() || 'N'}</div>;
        }

        // Default: try main image first
        return (
            <img
                src={websiteMetadata[newsItem.redirectUrl]?.image || websiteMetadata[newsItem.redirectUrl]?.favicon}
                alt={newsItem?.publisherName || 'Publisher'}
                className={styles.publisherImage}
                onError={() => handleMainImageError(index, newsItem.redirectUrl)}
            />
        );
    };

    const renderNewsCard = (newsItem: News, index: number) => (
        <div key={`news-${index}`} className={styles.newsReportContainer}>
            <div className={styles.newsCard}>
                <div className={styles.publisherImageContainer}>{renderPublisherImage(newsItem, index)}</div>
                <div className={styles.newsBody}>
                    {newsItem?.newsSentiment === 'Negative' && (
                        <div className={clsx(styles.sentiment, styles.negative)}>{newsItem.newsSentiment}</div>
                    )}

                    {newsItem?.title &&
                        (newsItem?.redirectUrl ? (
                            <a href={newsItem.redirectUrl} target="_blank" rel="noopener noreferrer" className={styles.newsTitle}>
                                {newsItem.title}
                            </a>
                        ) : (
                            <span className={styles.newsTitle}>{newsItem.title}</span>
                        ))}

                    <div className={styles.newsMeta}>
                        <div className={styles.metaLeft}>
                            {newsItem?.publisherName && <span className={styles.publisherName}>{newsItem.publisherName}</span>}
                        </div>
                        {newsItem?.publishedDate && <span className={styles.publishDate}>{newsItem.publishedDate}</span>}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderNewsContent = () => {
        const midpoint = Math.ceil(newsReport.news.length / 2);
        const leftNews = newsReport.news.slice(0, midpoint);
        const rightNews = newsReport.news.slice(midpoint);

        return (
            <div className={styles.newsReportSection}>
                <div className={styles.newsColumn}>{leftNews.map((newsItem, index) => renderNewsCard(newsItem, index))}</div>
                <div className={styles.columnDivider} />
                <div className={styles.newsColumn}>{rightNews.map((newsItem, index) => renderNewsCard(newsItem, index + midpoint))}</div>
            </div>
        );
    };

    return (
        <HeaderBodyBox
            title="News & Updates"
            headerRight={<SourceBadge tone={SOURCE_BADGE_TONE.NEUTRAL} value={OVERVIEW_SOURCE_LABELS.NEWS_SITES} />}
        >
            {renderNewsContent()}
        </HeaderBodyBox>
    );
};

export default NewsUpdatesV2;
